import { useNode, useUpdateNode } from "@/api/workflow-hooks";
import type { RPANode as RPANodeType } from "@repo/shared/rpa";
import type { RPAActionNode } from "@repo/shared/workflow";
import { produce } from "immer";
import {
  FileSearch,
  Globe,
  Keyboard,
  MousePointerClick,
  Play,
  Save,
  ScrollText,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  type Node,
  type Edge,
  type Connection,
  addEdge,
  Background,
  Controls,
  MiniMap,
  type NodeChange,
  type EdgeChange,
  MarkerType,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import JsonModal from "./JsonModal";
import LogModal, { type DownloadedFile } from "./LogModal";
import RPANode, { type ActionType } from "./RPANode";
import "reactflow/dist/style.css";

interface NodeData {
  label: string;
  actionType: ActionType;
  prompt: string;
  url?: string;
  selector?: string;
  text?: string;
  forceComputerUse?: boolean;
  onPromptChange?: (prompt: string) => void;
  onUrlChange?: (url: string) => void;
  onSelectorChange?: (selector: string) => void;
  onTextChange?: (text: string) => void;
  onLabelChange?: (label: string) => void;
  onForceComputerUseChange?: (force: boolean) => void;
  isSelected?: boolean;
  isEditingLabel?: boolean;
}

const nodeTypes = {
  rpaNode: RPANode,
};

const buttonBaseStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "8px 16px",
  backgroundColor: "white",
  color: "black",
  border: "1px solid rgba(0, 0, 0, 0.1)",
  borderRadius: "6px",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  fontSize: "14px",
  fontWeight: "500",
  transition: "all 0.2s",
  cursor: "pointer",
  ":hover": {
    backgroundColor: "rgba(0, 0, 0, 0.05)",
  },
};

const primaryButtonStyle = {
  ...buttonBaseStyle,
  backgroundColor: "black",
  color: "white",
  ":hover": {
    backgroundColor: "rgba(0, 0, 0, 0.8)",
  },
};

const menuButtonStyle = {
  ...buttonBaseStyle,
  backgroundColor: "lightgray", // Softer background color
  color: "black",
  padding: "10px 20px", // Added padding
  border: "1px solid rgba(0, 0, 0, 0.2)", // Softer border
  width: "200px",
  ":hover": {
    backgroundColor: "rgba(0, 0, 0, 0.1)", // Softer hover effect
  },
};

const createNodeCallbacks = (
  nodeId: string,
  setNodes: React.Dispatch<React.SetStateAction<Node<NodeData>[]>>,
  actionType: ActionType,
) => ({
  onPromptChange: (newPrompt: string) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId ? { ...n, data: { ...n.data, prompt: newPrompt } } : n,
      ),
    );
  },
  onUrlChange:
    actionType === "Navigate"
      ? (newUrl: string) => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId ? { ...n, data: { ...n.data, url: newUrl } } : n,
            ),
          );
        }
      : undefined,
  onSelectorChange: ["Click", "Type", "Extract"].includes(actionType)
    ? (newSelector: string) => {
        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, selector: newSelector } }
              : n,
          ),
        );
      }
    : undefined,
  onTextChange:
    actionType === "Type"
      ? (newText: string) => {
          setNodes((nds) =>
            nds.map((n) =>
              n.id === nodeId
                ? { ...n, data: { ...n.data, text: newText } }
                : n,
            ),
          );
        }
      : undefined,
  onLabelChange: (newLabel: string) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: { ...n.data, label: newLabel, isEditingLabel: false },
            }
          : n,
      ),
    );
  },
  onForceComputerUseChange: (force: boolean) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, forceComputerUse: force } }
          : n,
      ),
    );
  },
});

export default function WorkflowTab({
  workflowId,
  actionId,
}: { workflowId: string; actionId: string }) {
  const { data: definition } = useNode<RPAActionNode>(workflowId, actionId);
  const updateNodeMut = useUpdateNode(workflowId);

  const [nodes, setNodes] = useState<Node<NodeData>[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jsonToExport, setJsonToExport] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoadModalOpen, setIsLoadModalOpen] = useState(false); // State for the load modal
  const [jsonInput, setJsonInput] = useState("");
  const [logs, setLogs] = useState<string[]>([]); // State to hold logs
  const [downloadedFiles, setDownloadedFiles] = useState<DownloadedFile[]>([]);

  useEffect(() => {
    if (definition?.nodes && definition.edges) {
      loadTreeFromJson(definition.nodes, definition.edges); // Load the saved JSON data
    }
  }, [definition]);

  const defaultEdgeOptions = {
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 20,
      height: 20,
      color: "#666",
    },
    style: {
      stroke: "#666",
    },
  };
  const handleSaveWorkflow = () => {
    // Prepare the workflow data
    const workflowData = {
      nodes: nodes.map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          label: node.data.label,
          actionType: node.data.actionType,
          prompt: node.data.prompt,
          url: node.data.url,
          selector: node.data.selector,
          text: node.data.text,
          forceComputerUse: node.data.forceComputerUse,
        },
      })) as RPANodeType[],
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
      })),
    };

    if (!definition) {
      return;
    }

    const newNode = produce(definition, (draft) => {
      draft.nodes = workflowData.nodes;
      draft.edges = workflowData.edges;
    });

    updateNodeMut.mutateAsync({ nodeId: actionId, update: newNode });
    alert("Workflow saved successfully!");
  };

  const LoadJsonModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onLoadJson: () => void;
  }> = ({ isOpen, onClose, onLoadJson }) => {
    if (!isOpen) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "5px",
            width: "80%",
            maxWidth: "600px",
          }}
        >
          <h2>Load JSON</h2>
          <textarea
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            placeholder="Paste your JSON here"
            style={{
              width: "100%",
              height: "200px",
              marginBottom: "10px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "10px",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button
              onClick={() => {
                onLoadJson();
                onClose();
              }}
              style={{
                padding: "10px 20px",
                backgroundColor: "#4CAF50",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Load
            </button>
            <button
              onClick={onClose}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f44336",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds: Edge[]) => addEdge(params, eds)),
    [],
  );

  const handleLoadJson = () => {
    const parsedJson = JSON.parse(jsonInput);
    loadTreeFromJson(parsedJson.nodes, parsedJson.edges); // Call loadTreeFromJson with the input JSON
    setIsLoadModalOpen(false); // Close the modal after loading
  };

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes((nds: Node<NodeData>[]) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      return updatedNodes.map((node) => {
        const isSelected = changes.some(
          (change) =>
            change.type === "select" &&
            change.id === node.id &&
            change.selected,
        );
        return {
          ...node,
          data: { ...node.data, isSelected: isSelected },
        };
      });
    });
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    setEdges((eds: Edge[]) => applyEdgeChanges(changes, eds));
  }, []);

  const loadTreeFromJson = (nodes: Node[], edges: Edge[]) => {
    try {
      const nodesWithCallbacks = nodes.map((node) => ({
        ...node,
        data: {
          workflowId,
          ...node.data,
          ...createNodeCallbacks(node.id, setNodes, node.data.actionType),
        },
      }));

      setNodes(nodesWithCallbacks);
      setEdges(edges);
    } catch (error) {
      console.error("Error loading tree from JSON:", error);
      alert(
        `Failed to load tree from JSON. Please check the format. Error: ${(error as Error).message}`,
      );
    }
  };

  const addNewNode = useCallback((type: ActionType) => {
    const newNode: Node<NodeData> = {
      id: `${type}-${Date.now()}`,
      type: "rpaNode",
      position: { x: 250, y: 250 },
      data: {
        label: `New ${type} Node`,
        actionType: type,
        prompt: "",
        url: type === "Navigate" ? "" : undefined,
        selector: ["Click", "Type", "Extract"].includes(type) ? "" : undefined,
        text: type === "Type" ? "" : undefined,
        forceComputerUse: false,
        isSelected: false,
        isEditingLabel: false,
        ...createNodeCallbacks(`${type}-${Date.now()}`, setNodes, type),
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, []);

  const exportToJson = useCallback(() => {
    // Prepare the workflow data
    const workflowData = {
      nodes: nodes.map((node) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          label: node.data.label,
          actionType: node.data.actionType,
          prompt: node.data.prompt,
          url: node.data.url,
          selector: node.data.selector,
          text: node.data.text,
          forceComputerUse: node.data.forceComputerUse,
        },
      })),
      edges: edges.map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
      })),
    };

    // Convert to JSON string
    const jsonString = JSON.stringify(workflowData, null, 2);

    setJsonToExport(jsonString);
    setIsModalOpen(true);
    setShowDropdown(false);
  }, [nodes, edges]);

  return (
    <div
      style={{
        width: "100%",
        height: "90vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "10px",
          borderBottom: "1px solid #ddd",
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => addNewNode("Click")} style={buttonBaseStyle}>
            <MousePointerClick size={16} />
            Click
          </button>
          <button onClick={() => addNewNode("Type")} style={buttonBaseStyle}>
            <Keyboard size={16} />
            Type
          </button>
          <button
            onClick={() => addNewNode("Navigate")}
            style={buttonBaseStyle}
          >
            <Globe size={16} />
            Navigate
          </button>
          <button onClick={() => addNewNode("Extract")} style={buttonBaseStyle}>
            <FileSearch size={16} />
            Extract
          </button>
          <button onClick={() => addNewNode("Scroll")} style={buttonBaseStyle}>
            <ScrollText size={16} />
            Scroll
          </button>
        </div>

        <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              style={buttonBaseStyle}
            >
              ⋮
            </button>
            {showDropdown && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  backgroundColor: "transparent",
                  borderRadius: "6px",
                  zIndex: 1000,
                  width: "150px",
                  padding: "10px",
                }}
              >
                <button
                  onClick={() => {
                    exportToJson();
                    setShowDropdown(false);
                  }} // Close dropdown after export
                  style={{ ...menuButtonStyle, marginBottom: "2px" }}
                >
                  Export to JSON
                </button>
                <button
                  onClick={() => {
                    setIsLoadModalOpen(true);
                    setShowDropdown(false);
                  }} // Close dropdown after opening load modal
                  style={{ ...menuButtonStyle, marginBottom: "2px" }}
                >
                  Load Tree from JSON
                </button>
                <button
                  onClick={() => {
                    handleSaveWorkflow();
                    setShowDropdown(false);
                  }} // Close dropdown after saving
                  style={{ ...menuButtonStyle, marginBottom: "2px" }}
                >
                  <Save size={16} />
                  Save Workflow
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, position: "relative" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          defaultEdgeOptions={defaultEdgeOptions}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          minZoom={0.1}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.9 }}
          style={{ position: "absolute", width: "100%", height: "100%" }}
        >
          <Background />
          <Controls />
          <MiniMap />
        </ReactFlow>
      </div>
      <JsonModal
        isOpen={isModalOpen}
        jsonData={jsonToExport}
        onClose={() => setIsModalOpen(false)}
      />
      <LoadJsonModal
        isOpen={isLoadModalOpen}
        onClose={() => setIsLoadModalOpen(false)}
        onLoadJson={() => handleLoadJson()} // Pass the handleLoadJson function to the modal
      />
      <LogModal
        isOpen={logs.length > 0}
        onClose={() => {
          setLogs([]);
          setDownloadedFiles([]);
        }}
        logs={logs}
        downloadedFiles={downloadedFiles}
      />
    </div>
  );
}
